import { Box, Button, Card, CardContent, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useSnackbar } from "notistack";
import { format } from "date-fns";

import { apiConstants, getGeneric, post } from "../../common/constants";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { formatDate } from "../../common/util";
import { FormProvider, RHFDateField, RHFMultipleSelectField, RHFTextField } from "../../common/hook-form";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { useGetAllMerchantsIdAndNameQuery } from "../../redux/commonApiCalls";
import { LoadingButton } from "@mui/lab";
import PageTitle from "../../components/PageTitle";
import Loader from "../../common/Loader";
import MUIModal from "../../components/MuiModal";
import DownloadSharpIcon from '@mui/icons-material/DownloadSharp';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const toIsoDate = (d) => (d ? format(d, 'yyyy-MM-dd') : null);

function MerchantInvoiceCorrections() {
    const { enqueueSnackbar } = useSnackbar();

    const filterSchema = Yup.object().shape({
        merchant: Yup.string().nullable(),
        reportKey: Yup.string().nullable(),
        awbNumber: Yup.string().nullable(),
        uploadDateGT: Yup.date().nullable().typeError("Invalid date"),
        uploadDateLT: Yup.date().nullable().typeError("Invalid date")
            .when('uploadDateGT', (uploadDateGT, schema) =>
                uploadDateGT ? schema.min(uploadDateGT, "End date cannot be before start date") : schema
            ),
    });

    const methods = useForm({
        resolver: yupResolver(filterSchema),
        defaultValues: {
            merchant: '',
            reportKey: '',
            awbNumber: '',
            uploadDateGT: null,
            uploadDateLT: null,
        }
    });

    const { handleSubmit, reset } = methods;

    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterData, setFilterData] = useState({});
    const [reportOpen, setReportOpen] = useState(false);
    const [reasonOptions, setReasonOptions] = useState([]);

    const { data: merchants = [] } = useGetAllMerchantsIdAndNameQuery();

    useEffect(() => {
        getGeneric(apiConstants.INVOICE_CORRECTIONS_REASONS)
            .then((res) => {
                const list = res?.data?.responseObj || [];
                setReasonOptions(list.map((r) => ({ value: r.enum, label: r.description })));
            })
            .catch((err) => {
                console.log('Failed to load correction reasons', err);
            });
    }, []);

    const onSubmitFilter = (values) => {
        const payload = {};
        if (values.merchant) payload.merchantOid = values.merchant;
        if (values.reportKey) payload.reportKey = values.reportKey.trim();
        if (values.awbNumber) payload.awbNumber = values.awbNumber.trim();
        if (values.uploadDateGT) payload.uploadDateGT = toIsoDate(values.uploadDateGT);
        if (values.uploadDateLT) payload.uploadDateLT = toIsoDate(values.uploadDateLT);
        setPage(0);
        setFilterData(payload);
    };

    useEffect(() => {
        setLoading(true);
        const payload = {
            ...filterData,
            skip: page * sizePerPage,
            limit: sizePerPage,
        };
        post(apiConstants.INVOICE_CORRECTIONS_FILTER, payload)
            .then((res) => {
                const list = res?.data?.responseObj || res?.data?.data || [];
                setData(Array.isArray(list) ? list : []);
            })
            .catch((err) => {
                console.log(err);
                enqueueSnackbar(err?.response?.data?.message || 'Failed to fetch corrections', { variant: 'error' });
            })
            .finally(() => setLoading(false));
    }, [filterData, page, sizePerPage, enqueueSnackbar]);

    const handleResetFilter = () => {
        reset({ merchant: '', reportKey: '', awbNumber: '', uploadDateGT: null, uploadDateLT: null });
        setPage(0);
        setFilterData({});
    };

    const columns = [
        {
            field: 'merchantOid', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Merchant', flex: 1.1,
            renderCell: (params) => {
                const { row } = params;
                const merchantId = row?.merchantOid;
                const merchant = merchants?.find((m) => m.id === merchantId);
                return merchant ? merchant.name : (merchantId || '-');
            },
        },
        {
            field: 'reportKey', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Invoice Report Key', flex: 1.6,
        },
        {
            field: 'invoicePeriod', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Invoice Period', flex: 1, sortable: false,
            renderCell: (params) => {
                const { row } = params;
                const from = row?.startDate ? formatDate(row.startDate, { format: 'dd MMM yyyy' }) : null;
                const to = row?.endDate ? formatDate(row.endDate, { format: 'dd MMM yyyy' }) : null;
                if (!from && !to) return '-';
                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        lineHeight: 1.3,
                    }}>
                        <Box component="span" sx={{
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            color: 'text.primary',
                            whiteSpace: 'nowrap',
                        }}>
                            {from || '—'}
                        </Box>
                        <Box component="span" sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                            mt: 0.25,
                        }}>
                            {to || '—'}
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'uploadedBy', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Correction Uploaded By', flex: 1.1,
        },
        {
            field: 'uploadedAt', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Correction Uploaded At', flex: 1,
            renderCell: (params) => params.row?.uploadedAt ? formatDate(params.row.uploadedAt) : '-',
        },
        {
            field: 'actions', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Actions', flex: 0.7,
            renderCell: (params) => {
                const url = params.row?.uploadedFile?.signedLink;
                return (
                    <Tooltip title={url ? "Download Uploaded CSV" : "No file available"}>
                        <span>
                            <IconButton size="small" color="primary" disabled={!url} onClick={() => window.open(url)}>
                                <DownloadSharpIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <>
            {loading && <Loader />}
            <PageTitle>Uploaded Invoice Corrections</PageTitle>

            <MUIModal
                open={reportOpen}
                handleClose={() => setReportOpen(false)}
                title="Generate AWB Corrections Report"
                maxWidth="md"
                children={
                    <GenerateReportForm
                        reasonOptions={reasonOptions}
                        onClose={() => setReportOpen(false)}
                    />
                }
            />

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.72rem' }}>
                            Filter Corrections
                        </Typography>
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            onClick={() => setReportOpen(true)}
                        >
                            Generate Report
                        </Button>
                    </Box>

                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitFilter)}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <MerchantFilterCached />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RHFTextField name='reportKey' label='Report Key' />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <RHFTextField name='awbNumber' label='AWB Number' />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <RHFDateField name='uploadDateGT' label='Upload Date From' />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <RHFDateField name='uploadDateLT' label='Upload Date To' />
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                <LoadingButton variant="contained" size="small" type="submit">
                                    Submit
                                </LoadingButton>
                                <Button onClick={handleResetFilter} variant="contained" color='error' size="small">
                                    Reset
                                </Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>

            <ProzoDataGrid
                columns={columns}
                autoHeight={true}
                rows={data || []}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                setPage={setPage}
                page={page}
                pagination={true}
                hideFooterPagination={false}
                filterMode={"server"}
                rowHeight={70}
                rowCount={Number.MAX_VALUE}
                getRowId={(row) => row.id || row._id || `${row.reportKey}_${row.uploadedAt}`}
            />
        </>
    );
}

export default MerchantInvoiceCorrections;

const GenerateReportForm = ({ reasonOptions, onClose }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [submitting, setSubmitting] = useState(false);

    const schema = Yup.object().shape({
        merchant: Yup.string().nullable(),
        reportKey: Yup.string().nullable(),
        awbNumber: Yup.string().nullable(),
        correctionReasons: Yup.array().nullable(),
        uploadDateStart: Yup.date().nullable().typeError("Invalid date").required("Start date is required"),
        uploadDateEnd: Yup.date().nullable().typeError("Invalid date").required("End date is required")
            .when('uploadDateStart', (uploadDateStart, schema) =>
                uploadDateStart ? schema.min(uploadDateStart, "End date cannot be before start date") : schema
            ),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            merchant: '',
            reportKey: '',
            awbNumber: '',
            correctionReasons: [],
            uploadDateStart: null,
            uploadDateEnd: null,
        }
    });

    const { handleSubmit, reset } = methods;

    const onSubmit = async (values) => {
        const payload = {
            uploadDateStart: toIsoDate(values.uploadDateStart),
            uploadDateEnd: toIsoDate(values.uploadDateEnd),
        };
        if (values.merchant) payload.merchantOid = values.merchant;
        if (values.reportKey) payload.reportKey = values.reportKey.trim();
        if (values.awbNumber) payload.awbNumbers = [values.awbNumber.trim()];
        if (values.correctionReasons?.length) payload.correctionReasons = values.correctionReasons;

        setSubmitting(true);
        try {
            const res = await post(apiConstants.INVOICE_CORRECTIONS_REPORT, payload);
            if (res?.data?.status === 'SUCCESS') {
                const url = res?.data?.responseObj?.reportUrl;
                const rows = res?.data?.responseObj?.rowCount;
                if (url) {
                    window.open(url);
                    enqueueSnackbar(`Report ready (${rows ?? 0} rows)`, { variant: 'success' });
                    reset();
                    onClose();
                } else {
                    enqueueSnackbar('Report generated but no download URL returned', { variant: 'warning' });
                }
            } else {
                enqueueSnackbar(res?.data?.errorMsg || res?.data?.message || 'Failed to generate report', { variant: 'error' });
            }
        } catch (err) {
            console.log(err);
            enqueueSnackbar(err?.response?.data?.errorMsg || err?.response?.data?.message || 'Failed to generate report', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Date range is required. All other filters are optional and narrow the rows included in the generated CSV.
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <RHFDateField name='uploadDateStart' label='Upload Date From *' />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <RHFDateField name='uploadDateEnd' label='Upload Date To *' />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MerchantFilterCached />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <RHFTextField name='reportKey' label='Report Key' />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <RHFTextField name='awbNumber' label='AWB Number' />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <RHFMultipleSelectField
                            name='correctionReasons'
                            label='Correction Reasons'
                            options={reasonOptions}
                            width='100%'
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                    <LoadingButton
                        type="submit"
                        loading={submitting}
                        variant="contained"
                        color="success"
                        startIcon={<FileDownloadIcon />}
                    >
                        Generate Report
                    </LoadingButton>
                    <Button onClick={() => reset()} variant="outlined" color="inherit">
                        Reset
                    </Button>
                </Box>
            </Box>
        </FormProvider>
    );
};
